"use client";
import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useState } from "react";
import Alert from "./Alert";
import { signIn } from "next-auth/react";
import { EyeIcon, EyeSlashIcon } from "@heroicons/react/24/outline";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loggedIn, setLoggedIn] = useState("processing");

  const router = useRouter();

  const togglePasswordVisibility = () => setShowPassword(!showPassword);

  const requiresOtp = (email: string) =>
    email.toLowerCase().endsWith("@my.mzuni.ac.mw");

  const handleLogin = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const result = await signIn("credentials", {
        redirect: false,
        email,
        password,
      });

      console.log("LOGIN RESULT:", result);

      if (result?.error) {
        const errorMsg = result.error.toLowerCase();
        if (
          errorMsg.includes("invalid email") ||
          errorMsg.includes("password")
        ) {
          setLoggedIn("invalid");
        } else if (
          errorMsg.includes("not activated") ||
          errorMsg.includes("blocked") ||
          errorMsg.includes("restricted")
        ) {
          setLoggedIn("restricted");
          setError(errorMsg);
        } else {
          setLoggedIn("error");
        }
        return;
      }

      if (requiresOtp(email)) {
        setLoggedIn("success");
        router.push("/home");
        return;
      }

      const statusResponse = await fetch("/api/otp/status");
      if (!statusResponse.ok) {
        setLoggedIn("error");
        return;
      }

      const statusData = await statusResponse.json();
      if (!statusData.expired && statusData.verified === true) {
        setLoggedIn("success");
        router.push("/home");
      } else {
        router.push("/verify-me");
      }
    } catch (error) {
      console.error("Error in handleLogin:", error);
      setLoggedIn("error");
    } finally {
      setIsLoading(false);
    }
  };

  function renderLoginResponse() {
    let message = "";
    let style = "danger";
    if (loggedIn === "success") {
      message = "Success! Getting things ready…";
      style = "success";
    } else if (loggedIn === "invalid") {
      message = "Wrong email or password. Please check your credentials.";
      style = "danger";
    } else if (loggedIn === "error") {
      message = "An unexpected error occurred. Please try again later.";
      style = "danger";
    } else if (loggedIn === "restricted") {
      message = error || "Your account is restricted.";
      style = "danger";
    }
    return <Alert message={message} style={style} />;
  }

  return (
    <>
      {/* Top progress bar */}
      {isLoading && (
        <div className="fixed top-0 left-0 w-full h-1 bg-green-600 animate-pulse z-50" />
      )}

      <section className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-950 px-4">
        <div className="w-full max-w-sm">
          {/* Card */}
          <div className="bg-white/80 dark:bg-gray-900/60 backdrop-blur-xl border border-gray-200 dark:border-gray-700 rounded-2xl px-10 py-8 shadow-sm">
            {/* Logo + headings */}
            <div className="flex flex-col items-center mb-8">
              <Image
                src="/mzunilogo.png"
                alt="Mzuzu University"
                width={120}
                height={120}
                className="mb-4"
              />
              <h1 className="text-2xl font-normal text-gray-800 dark:text-gray-100 tracking-tight">
                Sign in to your account
              </h1>
              {/* <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                Use your Mzuzu University email and password
              </p> */}
            </div>

            <form onSubmit={handleLogin} className="space-y-5">
              {/* Email */}
              <div className="relative">
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  placeholder=" "
                  className="peer w-full px-3 pt-5 pb-2 text-sm rounded-md
                    bg-transparent
                    border border-gray-300 dark:border-gray-600
                    text-gray-900 dark:text-gray-100
                    focus:outline-none focus:border-green-600 focus:ring-1 focus:ring-green-600
                    dark:focus:border-green-500 dark:focus:ring-green-500
                    transition-colors"
                />
                <label
                  htmlFor="email"
                  className="absolute left-3 top-3.5 text-sm text-gray-500 dark:text-gray-400
                    transition-all duration-150 origin-left
                    peer-placeholder-shown:top-3.5 peer-placeholder-shown:text-sm
                    peer-focus:top-1 peer-focus:text-xs peer-focus:text-green-600 dark:peer-focus:text-green-400
                    peer-not-placeholder-shown:top-1 peer-not-placeholder-shown:text-xs"
                >
                  Email
                </label>
              </div>

              {/* Password */}
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  placeholder=" "
                  className="peer w-full px-3 pt-5 pb-2 pr-10 text-sm rounded-md
                    bg-transparent
                    border border-gray-300 dark:border-gray-600
                    text-gray-900 dark:text-gray-100
                    focus:outline-none focus:border-green-600 focus:ring-1 focus:ring-green-600
                    dark:focus:border-green-500 dark:focus:ring-green-500
                    transition-colors"
                />
                <label
                  htmlFor="password"
                  className="absolute left-3 top-3.5 text-sm text-gray-500 dark:text-gray-400
                    transition-all duration-150 origin-left
                    peer-placeholder-shown:top-3.5 peer-placeholder-shown:text-sm
                    peer-focus:top-1 peer-focus:text-xs peer-focus:text-green-600 dark:peer-focus:text-green-400
                    peer-not-placeholder-shown:top-1 peer-not-placeholder-shown:text-xs"
                >
                  Password
                </label>
                <button
                  type="button"
                  onClick={togglePasswordVisibility}
                  tabIndex={-1}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                >
                  {showPassword ? (
                    <EyeSlashIcon className="h-4 w-4" />
                  ) : (
                    <EyeIcon className="h-4 w-4" />
                  )}
                </button>
              </div>

              {/* Forgot password */}
              <div className="text-right -mt-1">
                <Link
                  href="/forgot-password"
                  className="text-sm font-medium text-green-700 dark:text-green-400 hover:text-green-800 dark:hover:text-green-300 hover:underline"
                >
                  Forgot password?
                </Link>
              </div>

              {/* Error / success alert */}
              {loggedIn !== "processing" && renderLoginResponse()}

              {/* Actions */}
              <div className="flex items-center justify-between pt-2">
                <button
                  type="submit"
                  disabled={isLoading}
                  className="ml-auto px-8 py-2.5 rounded-md text-sm font-medium
                    bg-green-700 hover:bg-green-800
                    dark:bg-green-600 dark:hover:bg-green-700
                    text-white shadow-sm
                    disabled:opacity-60 disabled:cursor-not-allowed
                    transition-colors duration-150"
                >
                  {isLoading ? "Signing in…" : "Login"}
                </button>
              </div>
            </form>
          </div>

          {/* Footer */}
          <p className="text-center text-xs text-gray-400 dark:text-gray-500 mt-6">
            &copy; {new Date().getFullYear()} Mzuzu University
          </p>
        </div>
      </section>
    </>
  );
}

// "use client";
// import { serverURL } from "@/app/ApiConfig";
// import Image from "next/image";
// import Link from "next/link";
// import { useRouter } from "next/navigation";
// import { useState } from "react";
// import Alert from "./Alert";
// import { signIn } from "next-auth/react";

// export default function Login() {
//   const [email, setEmail] = useState("");
//   const [password, setPassword] = useState("");
//   const [showPassword, setShowPassword] = useState(false);
//   const [isLoading, setIsLoading] = useState(false);
//   const [error, setError] = useState<string | null>(null);

//   const togglePasswordVisibility = () => {
//     setShowPassword(!showPassword);
//   };

//   const [loggedIn, setLoggedIn] = useState("processing");
//   const router = useRouter();

//   const handleLogin = async (e: React.FormEvent<HTMLFormElement>) => {
//     e.preventDefault();
//     setIsLoading(true);

//     try {
//       const result = await signIn("credentials", {
//         redirect: false,
//         email,
//         password,
//       });

//       if (result?.error) {
//         const errorMsg = result.error.toLowerCase();

//         if (
//           errorMsg.includes("invalid email") ||
//           errorMsg.includes("password")
//         ) {
//           setLoggedIn("invalid");
//         } else if (
//           errorMsg.includes("not activated") ||
//           errorMsg.includes("blocked") ||
//           errorMsg.includes("restricted")
//         ) {
//           setLoggedIn("restricted");
//           setError(errorMsg);
//         } else {
//           setLoggedIn("error");
//         }
//         return;
//       }

//       // ✅ Direct login - always go to home on successful authentication
//       setLoggedIn("success");
//       router.push("/home");
//     } catch (error) {
//       console.error("Error in handleLogin:", error);
//       setLoggedIn("error");
//     } finally {
//       setIsLoading(false);
//     }
//   };

//   function renderLoginResponse() {
//     let message = "";
//     let style = "danger";
//     if (loggedIn === "success") {
//       message = "Success! getting things ready..";
//       style = "success";
//     } else if (loggedIn === "invalid") {
//       message =
//         "Incorrect password or email. Check your credentials and try again";
//       style = "danger";
//     } else if (loggedIn === "error") {
//       message = "There was an unexpected server error. Please try again later";
//       style = "danger";
//     } else if (loggedIn === "restricted") {
//       message = error || "Your account is restricted.";
//       style = "danger";
//     }

//     return (
//       <>
//         <Alert message={message} style={style} />
//       </>
//     );
//   }

//   return (
//     <>
//       {isLoading && (
//         <div className="fixed top-0 left-0 w-full h-1 bg-green-600 animate-pulse z-50"></div>
//       )}

//       <section
//         className="relative h-screen flex items-center justify-center
//                       bg-gradient-to-br from-white via-gray-50 to-gray-100
//                       dark:from-gray-900 dark:via-gray-950 dark:to-black
//                       overflow-hidden"
//       >
//         {/* Background blobs */}
//         <div
//           className="absolute -top-20 -left-20 w-60 h-60
//                       bg-green-200 dark:bg-green-800
//                       rounded-full mix-blend-multiply filter blur-2xl opacity-20 animate-pulse"
//         ></div>
//         <div
//           className="absolute -bottom-20 -right-20 w-60 h-60
//                       bg-teal-200 dark:bg-teal-800
//                       rounded-full mix-blend-multiply filter blur-2xl opacity-20 animate-pulse"
//         ></div>

//         <div className="w-full max-w-md relative z-10">
//           <div
//             className={`rounded-2xl p-8 shadow-lg transition-all duration-500
//              ${isLoading ? "blur-sm pointer-events-none" : ""}
//              bg-white/60 dark:bg-gray-900/70
//              backdrop-blur-xl border border-white/50 dark:border-gray-700`}
//           >
//             {/* Logo */}
//             <div className="flex justify-center mb-6">
//               <Image
//                 src="/mzunilogo.png"
//                 alt="Institution logo"
//                 width={90}
//                 height={90}
//                 className="drop-shadow-sm"
//               />
//             </div>

//             <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100 text-center mb-6">
//               Sign in to your account
//             </h1>

//             <form onSubmit={handleLogin} className="space-y-5">
//               {/* Email */}
//               <div>
//                 <label
//                   htmlFor="email"
//                   className="block text-sm text-gray-600 dark:text-gray-300 mb-1"
//                 >
//                   Your email
//                 </label>
//                 <input
//                   type="email"
//                   id="email"
//                   value={email}
//                   onChange={(e) => setEmail(e.target.value)}
//                   required
//                   className="w-full p-3 rounded-lg
//                             bg-white/70 dark:bg-gray-800/70
//                             border border-gray-200 dark:border-gray-700
//                             text-gray-800 dark:text-gray-100
//                             placeholder-gray-400 dark:placeholder-gray-500
//                             focus:ring-2 focus:ring-green-400 focus:outline-none transition"
//                   placeholder="name@example.com"
//                 />
//               </div>

//               {/* Password */}
//               <div>
//                 <label
//                   htmlFor="password"
//                   className="block text-sm text-gray-600 dark:text-gray-300 mb-1"
//                 >
//                   Password
//                 </label>
//                 <div className="relative">
//                   <input
//                     type={showPassword ? "text" : "password"}
//                     id="password"
//                     value={password}
//                     onChange={(e) => setPassword(e.target.value)}
//                     required
//                     className="w-full p-3 rounded-lg
//                              bg-white/70 dark:bg-gray-800/70
//                              border border-gray-200 dark:border-gray-700
//                              text-gray-800 dark:text-gray-100
//                              placeholder-gray-400 dark:placeholder-gray-500
//                              focus:ring-2 focus:ring-green-400 focus:outline-none transition"
//                     placeholder="••••••••"
//                   />
//                   <button
//                     type="button"
//                     onClick={togglePasswordVisibility}
//                     className="absolute inset-y-0 right-3
//                              text-green-600 dark:text-green-400
//                              hover:text-green-700 dark:hover:text-green-300
//                              text-sm font-medium"
//                   >
//                     {showPassword ? "Hide" : "Show"}
//                   </button>
//                 </div>
//               </div>

//               {/* Options */}
//               <div className="flex items-center justify-between text-sm text-gray-600 dark:text-gray-400">
//                 <label className="flex items-center">
//                   <input
//                     type="checkbox"
//                     className="mr-2 w-4 h-4 text-green-500 border-gray-300 rounded focus:ring-green-400"
//                   />
//                   Remember me
//                 </label>
//                 <Link
//                   href="/forgot-password"
//                   className="hover:text-green-600 dark:hover:text-green-400"
//                 >
//                   Forgot password?
//                 </Link>
//               </div>

//               {/* Button */}
//               <button
//                 type="submit"
//                 className="w-full bg-gradient-to-r from-green-400 to-emerald-500
//                         hover:from-green-500 hover:to-emerald-600
//                         text-white py-3 rounded-lg font-semibold shadow-md
//                         hover:shadow-lg transform hover:scale-105
//                         transition-all duration-300"
//               >
//                 Sign in
//               </button>

//               {loggedIn !== "processing" && renderLoginResponse()}
//             </form>
//           </div>
//         </div>
//       </section>
//     </>
//   );
// }

// "use client";
// import { serverURL } from "@/app/ApiConfig";
// import Image from "next/image";
// import Link from "next/link";
// import { redirect, useRouter } from "next/navigation";
// import { useState } from "react";
// import Alert from "./Alert";
// import { getSession, signIn } from "next-auth/react";

// interface AxiosError {
//   message: string;
//   name: string;
//   stack: string;
//   config: object;
//   code: string;
//   status: number;
//   response: {
//     data: {
//       message: string;
//     };
//     status: number;
//   };
// }

// export default function Login() {
//   const [email, setEmail] = useState("");
//   const [password, setPassword] = useState("");
//   const [showPassword, setShowPassword] = useState(false); // Track password visibility
//   const [isLoading, setIsLoading] = useState(false);
//   const [error, setError] = useState<string | null>(null);

//   const togglePasswordVisibility = () => {
//     setShowPassword(!showPassword);
//   };
//   const [loggedIn, setLoggedIn] = useState("processing");
//   const router = useRouter();

//   const handleLogin = async (e: React.FormEvent<HTMLFormElement>) => {
//     e.preventDefault();
//     setIsLoading(true);

//     try {
//       const result = await signIn("credentials", {
//         redirect: false,
//         email,
//         password,
//       });

//       if (result?.error) {
//         console.error("Error during signIn:", result.error);

//         const errorMsg = result.error.toLowerCase();

//         if (
//           errorMsg.includes("invalid email") ||
//           errorMsg.includes("password")
//         ) {
//           setLoggedIn("invalid");
//         } else if (
//           errorMsg.includes("not activated") ||
//           errorMsg.includes("blocked") ||
//           errorMsg.includes("restricted")
//         ) {
//           setLoggedIn("restricted");
//           setError(errorMsg);
//         } else {
//           setLoggedIn("error");
//         }

//         return;
//       }

//       // Check OTP status securely via API route
//       const statusResponse = await fetch("/api/otp/status");

//       if (!statusResponse.ok) {
//         console.error(
//           "Failed to check OTP status:",
//           await statusResponse.text()
//         );
//         setLoggedIn("error");
//         return;
//       }

//       const statusData = await statusResponse.json();

//       console.log("OTP Status Data:", statusData);

//       if (!statusData.expired && statusData.verified === true) {
//         setLoggedIn("success");
//         router.push("/home");
//       } else {
//         router.push("/verify-me");
//       }
//     } catch (error) {
//       console.error("Error in handleLogin:", error);
//       setLoggedIn("error");
//     } finally {
//       setIsLoading(false);
//     }
//   };

//   const handleEmailChange = (event: { target: { value: any } }) => {
//     setEmail(event.target.value);
//   };

//   // Function to handle changes in the password input field
//   const handlePasswordChange = (event: { target: { value: any } }) => {
//     setPassword(event.target.value);
//   };

//   function renderLoginResponse() {
//     let message = "";
//     let style = "danger";
//     if (loggedIn === "success") {
//       message = "Success! getting things ready..";
//       style = "success";
//     } else if (loggedIn === "invalid") {
//       message =
//         "Incorrect password or email. Check your credentials and try again";
//       style = "danger";
//     } else if (loggedIn === "error") {
//       message = "There was an unexpected server error. Please try again later";
//       style = "danger";
//     } else if (loggedIn === "restricted") {
//       message = error || "Your account is restricted.";
//       style = "danger";
//     }

//     return (
//       <>
//         <Alert message={message} style={style} />
//       </>
//     );
//   }

//   return (
//     <>
//       {/* Top loading bar */}
//       {isLoading && (
//         <div className="fixed top-0 left-0 w-full h-1 bg-green-600 animate-pulse z-50"></div>
//       )}

//       <section
//         className="relative h-screen flex items-center justify-center
//                         bg-gradient-to-br from-white via-gray-50 to-gray-100
//                         dark:from-gray-900 dark:via-gray-950 dark:to-black
//                         overflow-hidden"
//       >
//         {/* Softer animated background blobs */}
//         <div
//           className="absolute -top-20 -left-20 w-60 h-60
//                       bg-green-200 dark:bg-green-900
//                       rounded-full mix-blend-multiply filter blur-2xl opacity-20 animate-pulse"
//         ></div>
//         <div
//           className="absolute -bottom-20 -right-20 w-60 h-60
//                       bg-teal-200 dark:bg-teal-900
//                       rounded-full mix-blend-multiply filter blur-2xl opacity-20 animate-pulse"
//         ></div>

//         <div className="w-full max-w-md relative z-10">
//           <div
//             className={`rounded-2xl p-8 shadow-lg transition-all duration-500
//           ${isLoading ? "blur-sm pointer-events-none" : ""}
//           bg-white/60 dark:bg-gray-900/70
//           backdrop-blur-xl border border-white/50 dark:border-gray-700`}
//           >
//             {/* Logo */}
//             <div className="flex justify-center mb-6">
//               <Image
//                 src="/mzunilogo.png"
//                 alt="Institution logo"
//                 width={90}
//                 height={90}
//                 className="drop-shadow-sm"
//               />
//             </div>

//             <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100 text-center mb-6">
//               Sign in to your account
//             </h1>

//             <form onSubmit={handleLogin} className="space-y-5">
//               {/* Email */}
//               <div>
//                 <label
//                   htmlFor="email"
//                   className="block text-sm text-gray-600 dark:text-gray-300 mb-1"
//                 >
//                   Your email
//                 </label>
//                 <input
//                   type="email"
//                   id="email"
//                   value={email}
//                   onChange={(e) => setEmail(e.target.value)}
//                   required
//                   className="w-full p-3 rounded-lg
//                            bg-white/70 dark:bg-gray-800/70
//                            border border-gray-200 dark:border-gray-700
//                            text-gray-800 dark:text-gray-100
//                            placeholder-gray-400 dark:placeholder-gray-500
//                            focus:ring-2 focus:ring-green-400 focus:outline-none transition"
//                   placeholder="name@example.com"
//                 />
//               </div>

//               {/* Password */}
//               <div>
//                 <label
//                   htmlFor="password"
//                   className="block text-sm text-gray-600 dark:text-gray-300 mb-1"
//                 >
//                   Password
//                 </label>
//                 <div className="relative">
//                   <input
//                     type={showPassword ? "text" : "password"}
//                     id="password"
//                     value={password}
//                     onChange={(e) => setPassword(e.target.value)}
//                     required
//                     className="w-full p-3 rounded-lg
//                              bg-white/70 dark:bg-gray-800/70
//                              border border-gray-200 dark:border-gray-700
//                              text-gray-800 dark:text-gray-100
//                              placeholder-gray-400 dark:placeholder-gray-500
//                              focus:ring-2 focus:ring-green-400 focus:outline-none transition"
//                     placeholder="••••••••"
//                   />
//                   <button
//                     type="button"
//                     onClick={togglePasswordVisibility}
//                     className="absolute inset-y-0 right-3
//                              text-green-600 dark:text-green-400
//                              hover:text-green-700 dark:hover:text-green-300
//                              text-sm font-medium"
//                   >
//                     {showPassword ? "Hide" : "Show"}
//                   </button>
//                 </div>
//               </div>

//               {/* Options */}
//               <div className="flex items-center justify-between text-sm text-gray-600 dark:text-gray-400">
//                 <label className="flex items-center">
//                   <input
//                     type="checkbox"
//                     className="mr-2 w-4 h-4 text-green-500 border-gray-300 rounded focus:ring-green-400"
//                   />
//                   Remember me
//                 </label>
//                 <Link
//                   href="/forgot-password"
//                   className="hover:text-green-600 dark:hover:text-green-400"
//                 >
//                   Forgot password?
//                 </Link>
//               </div>

//               {/* Button */}
//               <button
//                 type="submit"
//                 className="w-full bg-gradient-to-r from-green-400 to-emerald-500
//                          hover:from-green-500 hover:to-emerald-600
//                          text-white py-3 rounded-lg font-semibold shadow-md
//                          hover:shadow-lg transform hover:scale-105
//                          transition-all duration-300"
//               >
//                 Sign in
//               </button>

//               {loggedIn !== "processing" && renderLoginResponse()}
//             </form>
//           </div>
//         </div>
//       </section>
//     </>
//   );
// }

// "use client";
// import { serverURL } from "@/app/ApiConfig";
// import Image from "next/image";
// import Link from "next/link";
// import { redirect, useRouter } from "next/navigation";
// import { useState } from "react";
// import Alert from "./Alert";
// import { getSession, signIn } from "next-auth/react";

// interface AxiosError {
//   message: string;
//   name: string;
//   stack: string;
//   config: object;
//   code: string;
//   status: number;
//   response: {
//     data: {
//       message: string;
//     };
//     status: number;
//   };
// }

// export default function Login() {
//   const [email, setEmail] = useState("");
//   const [password, setPassword] = useState("");
//   const [showPassword, setShowPassword] = useState(false); // Track password visibility
//   const [isLoading, setIsLoading] = useState(false);

//   const togglePasswordVisibility = () => {
//     setShowPassword(!showPassword);
//   };
//   const [loggedIn, setLoggedIn] = useState("processing");
//   const router = useRouter();

//   const handleLogin = async (e: React.FormEvent<HTMLFormElement>) => {
//     e.preventDefault();
//     setIsLoading(true);

//     try {
//       const result = await signIn("credentials", {
//         redirect: false,
//         email,
//         password,
//       });

//       if (result?.error) {
//         console.error("Error during signIn:", result.error);
//         if (result.error === "Invalid email or password") {
//           setLoggedIn("invalid");
//         } else {
//           setLoggedIn("error");
//         }
//         return;
//       }

//       // Check OTP status securely via API route
//       const statusResponse = await fetch("/api/otp/status");

//       if (!statusResponse.ok) {
//         console.error(
//           "Failed to check OTP status:",
//           await statusResponse.text()
//         );
//         setLoggedIn("error");
//         return;
//       }

//       const statusData = await statusResponse.json();

//       if (!statusData.expired && statusData.verified === true) {
//         setLoggedIn("success");
//         router.push("/home");
//       } else {
//         router.push("/verify-me");
//       }
//     } catch (error) {
//       console.error("Error in handleLogin:", error);
//       setLoggedIn("error");
//     } finally {
//       setIsLoading(false);
//     }
//   };

//   const handleEmailChange = (event: { target: { value: any } }) => {
//     setEmail(event.target.value);
//   };

//   // Function to handle changes in the password input field
//   const handlePasswordChange = (event: { target: { value: any } }) => {
//     setPassword(event.target.value);
//   };

//   function renderLoginResponse() {
//     let message = "";
//     let style = "danger";
//     if (loggedIn === "success") {
//       message = "Success! getting things ready..";
//       style = "success";
//     } else if (loggedIn === "invalid") {
//       message =
//         "Incorrect password or email. Check your credentials and try again";
//       style = "danger";
//     } else if (loggedIn === "error") {
//       message = "There was an unexpected server error. Please try again later";
//       style = "danger";
//     }

//     return (
//       <>
//         <Alert message={message} style={style} />
//       </>
//     );
//   }

//   return (
//     <>
//       {/* Top loading bar */}
//       {isLoading && (
//         <div className="fixed top-0 left-0 w-full h-1 bg-green-600 animate-pulse z-50"></div>
//       )}

//       <section className="bg-gray-50 dark:bg-gray-900 h-screen flex items-center justify-center">
//         <div className="w-full max-w-md">
//           <div
//             className={`bg-white dark:bg-gray-800 rounded-2xl drop-shadow-2xl p-6 transition-all duration-300 ${
//               isLoading ? "blur-sm pointer-events-none" : ""
//             }`}
//           >
//             <div className="flex justify-center mb-6">
//               <Image
//                 src="/mzunilogo.png"
//                 alt="Institution logo"
//                 width={120}
//                 height={120}
//                 className="w-auto h-auto"
//               />
//             </div>
//             <h1 className="text-xl font-bold text-gray-900 dark:text-white text-center mb-4">
//               Sign in to your account
//             </h1>
//             <form onSubmit={handleLogin} className="space-y-4">
//               <div>
//                 <label
//                   htmlFor="email"
//                   className="block text-sm font-medium text-gray-900 dark:text-white"
//                 >
//                   Your email
//                 </label>
//                 <input
//                   type="email"
//                   id="email"
//                   name="email"
//                   value={email}
//                   onChange={(e) => setEmail(e.target.value)}
//                   required
//                   className="w-full p-2.5 border rounded-lg bg-gray-50 dark:bg-gray-700 dark:text-white dark:border-gray-600 focus:ring-green-500 focus:border-green-500"
//                 />
//               </div>
//               <div>
//                 <label
//                   htmlFor="password"
//                   className="block text-sm font-medium text-gray-900 dark:text-white"
//                 >
//                   Password
//                 </label>
//                 <div className="relative">
//                   <input
//                     type={showPassword ? "text" : "password"}
//                     id="password"
//                     name="password"
//                     value={password}
//                     onChange={(e) => setPassword(e.target.value)}
//                     required
//                     className="w-full p-2.5 border rounded-lg bg-gray-50 dark:bg-gray-700 dark:text-white dark:border-gray-600 focus:ring-green-500 focus:border-green-500"
//                   />
//                   <button
//                     type="button"
//                     className="absolute inset-y-0 right-0 pr-3 flex items-center text-sm font-medium text-green-600 dark:text-green-500"
//                     onClick={togglePasswordVisibility}
//                   >
//                     {showPassword ? "Hide" : "Show"}
//                   </button>
//                 </div>
//               </div>
//               <div className="flex items-center justify-between">
//                 <label className="flex items-center text-sm text-gray-500 dark:text-gray-300">
//                   <input
//                     type="checkbox"
//                     className="mr-2 w-4 h-4 rounded focus:ring-green-500"
//                   />
//                   Remember me
//                 </label>
//                 <Link
//                   href="/forgot-password"
//                   className="text-sm text-green-600 dark:text-green-500 hover:underline"
//                 >
//                   Forgot password?
//                 </Link>
//               </div>
//               <button
//                 type="submit"
//                 className="w-full bg-green-600 text-white py-2.5 rounded-lg font-medium hover:bg-green-700 transition-colors focus:ring-4 focus:ring-green-300 dark:focus:ring-green-800"
//               >
//                 Sign in
//               </button>
//               {loggedIn !== "processing" && renderLoginResponse()}
//             </form>
//           </div>
//         </div>
//       </section>
//     </>
//   );
// }
